import React, { useEffect, useState } from "react";
import {
  NavLink,
  useHistory,
  useParams,
  useQueryParamString
} from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { useTranslation } from "utilities/i18n";
import { Card, Field, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import { SortState } from "store/filters";
import {
  sortTableData,
  TableBody,
  TableBulkItemRow,
  TableHeaderItem
} from "collections/Table";
import {
  CategoryTitle,
  DropdownList,
  Loader,
  NoResultsIndicator,
  OptionType,
  PolyIcon
} from "elements";
import { toLicenseDateLocaleDateString } from "utilities/licenseServerUtils";
import { humanReadableBytes } from "utilities/numberUtils";
import { handleNullFields } from "utilities/stringUtils";
import { History } from "history";
import {
  ProductVersions,
  useGetProductSoftwareCatalog,
  useGetProductVersions
} from "../useProductCatalog";

export function getProductDetailsLink<S>(
  productId: string,
  version?: string
): History.LocationDescriptor<S> {
  const result: History.LocationDescriptor<S> = {};
  if (productId) {
    result.pathname = `/manage/licensing/clariti/product-catalog/details/${productId}`;
    if (version) {
      result.search = `?version=${version}`;
    }
  } else {
    result.pathname = "/manage/licensing/clariti/product-catalog";
  }
  return result;
}

export function getProductIdFromLink(id?: string) {
  return id ?? ""; // decodeURIComponent(id ?? "");
}

export const ProductInformation: React.FC<{
  product: ProductVersions;
}> = ({ product }) => {
  const { t } = useTranslation();

  const fields: Field[] = [
    {
      key: "name",
      label: t("NAME"),
      content: <p>{handleNullFields(product.name)}</p>
    },
    {
      key: "latestVersion",
      label: t("LATEST_VERSION"),
      content: <p>{handleNullFields(product.latestVersion)}</p>
    }
  ];

  return (
    <Section>
      <Section.Header>{t("PRODUCT_INFORMATION")}</Section.Header>
      <Card className="table-card full-width">
        {makeColumnContentForCard(fields, 2)}
      </Card>
    </Section>
  );
};

export const ProductVersionsTable: React.FC<{
  product: ProductVersions;
  version: string;
  setVersion: (value: string) => void;
  fetching: boolean;
}> = ({ product, version, setVersion, fetching }) => {
  const { t } = useTranslation();

  const productVersionOptions: OptionType[] =
    product.versions
      .map(productVersion => ({
        value: productVersion,
        text: productVersion ?? t("UNKNOWN")
      }))
      .reverse() ?? [];

  return (
    <>
      <Section className="mb-2">
        <Section.Header>{t("AVAILABLE_VERSIONS")}</Section.Header>
        <Section.Content>
          {t("PRODUCT_AVAILABLE_VERSIONS_DESC")}
        </Section.Content>
      </Section>
      <div className="mb-2">
        <DropdownList
          className="ph-0"
          fitted
          options={productVersionOptions}
          name="product-versions"
          onChange={setVersion}
          value={version}
          search
          busy={fetching}
        />
      </div>
    </>
  );
};

export const SoftwareArtifactsTable: React.FC<{
  productId: string;
  version: string;
}> = ({ productId, version }) => {
  const { t } = useTranslation();
  const [currentSort, setSort] = useState<SortState>({
    field: "artifactName",
    direction: "ASC"
  });
  const { data, fetching } = useGetProductSoftwareCatalog({
    variables: {
      productId,
      version
    }
  });

  const tableData = data?.softwareArtifacts ?? [];
  sortTableData(tableData, currentSort);

  const columns = [
    {
      key: "artifactName",
      label: t("ARTIFACT_NAME"),
      className: "flex-half-width"
    },
    {
      key: "fileSize",
      label: t("FILE_SIZE"),
      className: "file-size"
    },
    {
      key: "lastModifiedTime",
      label: t("LAST_MODIFIED"),
      className: "last-modified-time"
    }
  ];

  return (
    <>
      <Section>
        <Section.Header>
          {t("ARTIFACTS")} <span className="page-subheader">{version}</span>
        </Section.Header>
      </Section>
      <div className="table-container">
        <table className="table custom-table">
          <thead>
            <tr>
              {columns.map(({ key, label, className }) => (
                <TableHeaderItem
                  fieldName={key}
                  title={label}
                  sort={{ currentSort, onSort: setSort }}
                  key={key}
                  className={className}
                />
              ))}
            </tr>
          </thead>
          <TableBody
            loading={fetching}
            cols={columns.length - 1}
            accommodateScrollbar
          >
            {tableData.map(rowData => {
              return (
                <TableBulkItemRow
                  key={rowData.artifactName}
                  showChecks={false}
                  onCheck={() => {}}
                  checked={false}
                >
                  <td className={columns[0]?.className}>
                    <a href={rowData.url} className="font-sm" download>
                      {rowData.artifactName}
                      <PolyIcon
                        icon="download_arrow"
                        className="download_arrow pl-5"
                      />
                    </a>
                  </td>
                  <td className={columns[1]?.className}>
                    {humanReadableBytes(rowData.fileSize)}
                  </td>
                  <td className={columns[2]?.className}>
                    {toLicenseDateLocaleDateString(rowData.lastModifiedTime)}
                  </td>
                </TableBulkItemRow>
              );
            })}
            {!fetching && !tableData.length && (
              <tr>
                <td>
                  <NoResultsIndicator
                    className="inline mt-5"
                    title={t("NO_ARTIFACTS_FOUND")}
                  />
                </td>
              </tr>
            )}
          </TableBody>
        </table>
      </div>
    </>
  );
};

export const ProductCatalogDetails: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const productId = id ?? "";

  const [
    selectedVersion,
    setSelectedVersion,
    clearArtifactVersion
  ] = useQueryParamString<"version">("version");

  const { data, fetching } = useGetProductVersions({
    variables: {
      productId: productId
    }
  });

  const product: ProductVersions | undefined = data;

  useEffect(() => {
    if (!fetching) {
      if (!product || productId !== product.productId) {
        history.push("/manage/licensing/clariti/product-catalog");
      }
    }
  }, [fetching, productId, product, history]);

  const latestVersion = product?.latestVersion ?? "";
  const aSelectedVersion = selectedVersion ?? latestVersion;

  const { Sidebar, Content } = Layout;
  return (
    <>
      <Sidebar>
        <span className="title-capitalized">{t("CLARITI")}</span>
        <ul className="menu router">
          <li>
            <NavLink nest exact to={""}>
              {t("OVERVIEW")}
            </NavLink>
          </li>
        </ul>
      </Sidebar>
      <Content
        className="product-catalog-details"
        data-testid="product-catalog-details"
      >
        <Loader loading={fetching}>
          {product && (
            <>
              <Content.Title>
                <CategoryTitle
                  title={t("OVERVIEW")}
                  subtitle={handleNullFields(product.name)}
                />
              </Content.Title>
              <ProductInformation product={product} />
              <ProductVersionsTable
                product={product}
                version={aSelectedVersion}
                setVersion={value => {
                  if (value !== latestVersion) {
                    setSelectedVersion(value);
                  } else {
                    clearArtifactVersion();
                  }
                }}
                fetching={fetching}
              />
              <SoftwareArtifactsTable
                productId={productId}
                version={aSelectedVersion}
              />
            </>
          )}
        </Loader>
      </Content>
    </>
  );
};
